/**
 * @file Controlled drug export modal.
 */

import { appData } from '../../app/state/state.js';
import {
    closeForm,
    formModalElement,
    setModalCloseCallback,
} from '../../shared/forms/modalShell.js';
import {
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
} from "../../shared/forms/nativeFieldStyles.js";
import {
    getLocalDateString,
} from "../../shared/utils/dateUtils.js";
import { createDateRangePreset } from "../../shared/ui/controls/controlPresets.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import {
    sortDrugsByOrder,
    sortLocationsByOrder,
    sortUsersByDrugOrder,
} from "./drugUtils.js";
import {
    applyDrugControlSurface,
    bindDrugNativeFieldSurface,
    DRUG_CANCEL_BUTTON_CLASSES,
    getDrugNativeFieldHtml,
    validateDrugNativeFields,
} from "./drugControlStyles.js";
import { exportDrugData } from './drugApi.js';
import { printReport } from './drugExportReports.js';

const EXPORT_FILTER_SEARCH_THRESHOLD = 7;
const EXPORT_ACTION_BUTTON_CLASSES = [
    "inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-gray-200",
    "bg-white px-4 py-2 text-sm font-medium text-gray-600 transition-colors",
    "hover:border-blue-500 hover:text-blue-600 focus:outline-none",
    "focus-visible:border-blue-500 focus-visible:text-blue-600",
    "disabled:cursor-not-allowed disabled:opacity-50",
].join(" ");

const DATE_PRESET_OPTIONS = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_7_days', label: 'Last 7 days' },
    { value: 'last_30_days', label: 'Last 30 days' },
    { value: 'week_to_date', label: 'Week to date' },
    { value: 'month_to_date', label: 'Month to date' },
    { value: 'year_to_date', label: 'Year to date' },
    { value: 'fy_to_date', label: 'Financial year to date' },
    { value: 'last_week', label: 'Last week' },
    { value: 'last_month', label: 'Last month' },
    { value: 'last_year', label: 'Last year' },
    { value: 'last_fy', label: 'Last financial year' },
    { value: 'all_time', label: 'All time' },
    { value: 'custom', label: 'Custom range' },
];

function getDatePresetRange(preset) {
    const now = new Date();
    const todayStr = getLocalDateString(now);
    const fmt = (dateObj) => getLocalDateString(dateObj);
    let start = todayStr;
    let end = todayStr;

    switch (preset) {
        case 'today':
            break;
        case 'yesterday':
            now.setDate(now.getDate() - 1);
            start = fmt(now);
            end = start;
            break;
        case 'last_7_days':
            now.setDate(now.getDate() - 6);
            start = fmt(now);
            break;
        case 'last_30_days':
            now.setDate(now.getDate() - 29);
            start = fmt(now);
            break;
        case 'week_to_date': {
            const day = now.getDay() || 7;
            now.setHours(0, 0, 0, 0);
            if (day !== 1) {now.setDate(now.getDate() - (day - 1));}
            start = fmt(now);
            break;
        }
        case 'month_to_date':
            now.setDate(1);
            start = fmt(now);
            break;
        case 'year_to_date':
            start = `${now.getFullYear()}-01-01`;
            break;
        case 'fy_to_date':
            start = now.getMonth() < 3 ?
                `${now.getFullYear() - 1}-04-01` :
                `${now.getFullYear()}-04-01`;
            break;
        case 'last_week': {
            const lastSunday = new Date();
            const currentDay = lastSunday.getDay() || 7;
            lastSunday.setDate(lastSunday.getDate() - currentDay);
            end = fmt(lastSunday);
            const lastMonday = new Date(lastSunday);
            lastMonday.setDate(lastSunday.getDate() - 6);
            start = fmt(lastMonday);
            break;
        }
        case 'last_month':
            now.setDate(0);
            end = fmt(now);
            now.setDate(1);
            start = fmt(now);
            break;
        case 'last_year':
            start = `${now.getFullYear() - 1}-01-01`;
            end = `${now.getFullYear() - 1}-12-31`;
            break;
        case 'last_fy':
            if (now.getMonth() < 3) {
                start = `${now.getFullYear() - 2}-04-01`;
                end = `${now.getFullYear() - 1}-03-31`;
            } else {
                start = `${now.getFullYear() - 1}-04-01`;
                end = `${now.getFullYear()}-03-31`;
            }
            break;
        case 'all_time':
            start = '2020-01-01';
            break;
        default:
            return null;
    }

    return { start, end };
}

function toExportOption(item) {
    const value = String(item?.id || item?.uid || item?.value || "");
    const baseLabel = item?.label || item?.shortName || item?.name || value;
    const label = item?.isActive === false ? `${baseLabel} (inactive)` : baseLabel;
    return {
        value,
        label,
        inactive: false,
        order: Number(item?.order ?? 9999),
        searchText: `${label} ${value}`,
    };
}

function getExportControlValue(control) {
    return Array.isArray(control?.value) ? control.value : [];
}

function getExportOptionValues(options = []) {
    return options.map((option) => String(option.value)).filter(Boolean);
}

function getFilteredSelection(control, options = []) {
    const selectedValues = getExportControlValue(control);
    const optionValues = getExportOptionValues(options);
    if (optionValues.length === 0 || selectedValues.length !== optionValues.length) {
        return selectedValues;
    }

    const selectedSet = new Set(selectedValues.map((value) => String(value)));
    return optionValues.every((value) => selectedSet.has(value)) ? [] : selectedValues;
}

function keepSelectedExportValues(control, options = []) {
    const optionValues = getExportOptionValues(options);
    const optionSet = new Set(optionValues);
    return getExportControlValue(control).filter((value) => optionSet.has(String(value)));
}

/**
 * Opens the drug data export modal.
 * @returns {void}
 */
export function openExportModal() {
    if (!formModalElement) {return;}

    const formTitle = formModalElement.querySelector('#form-title');
    const formHeaderActions = formModalElement.querySelector('#form-header-actions');
    const formFields = formModalElement.querySelector('#form-fields');
    const itemForm = formModalElement.querySelector('#item-form');
    const submitBtn = formModalElement.querySelector('button[type="submit"]');
    const cancelBtn = document.getElementById('cancel-form-btn');
    const footer = cancelBtn?.parentElement || submitBtn?.parentElement || null;

    if (!formTitle || !formFields || !itemForm || !submitBtn || !cancelBtn || !footer) {
        console.error('Export modal requires the shared form modal shell.');
        return;
    }

    formTitle.textContent = 'Export Drug Data';
    itemForm.dataset.type = 'export';
    itemForm.dataset.id = '';
    itemForm.dataset.parentId = '';
    applyDrugControlSurface(formModalElement, appData);
    applyDrugControlSurface(itemForm, appData);
    if (formHeaderActions) {formHeaderActions.innerHTML = '';}

    const previousSubmitHandler = itemForm.onsubmit;
    itemForm.onsubmit = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };
    setModalCloseCallback(() => {
        itemForm.onsubmit = previousSubmitHandler;
    });

    const today = getLocalDateString();
    const firstDay = new Date();
    firstDay.setDate(1);
    const firstDayStr = getLocalDateString(firstDay);

    const allDrugs = sortDrugsByOrder(appData.drugInventory || []);
    const allLocations = sortLocationsByOrder(appData.config?.drugs?.locations || []);
    const allUsers = sortUsersByDrugOrder(appData.users || []);
    const activeDrugs = allDrugs.filter(d => d.isActive !== false);
    const activeLocations = allLocations.filter(l => l.isActive !== false);
    const activeUsers = allUsers.filter(u => u.isActive !== false);
    const typeOptions = [
        { id: 'use', name: 'Use' },
        { id: 'restock', name: 'Restock' },
        { id: 'relocate', name: 'Relocate' },
        { id: 'correction', name: 'Correction' },
        { id: 'loss', name: 'Loss' },
        { id: 'void', name: 'Void' }
    ];

    formFields.innerHTML = `
        <div class="space-y-4">
            <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <div class="min-w-0 space-y-3">
                    <label class="block text-base text-gray-500 sm:text-sm">Date Range</label>
                    <div id="export-date-preset-control"></div>
                    <div id="export-date-range-control"></div>
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                ${getDrugNativeFieldHtml('Transaction Type', '<div id="export-type-control"></div>')}
                ${getDrugNativeFieldHtml('Drug', '<div id="export-drug-control"></div>')}
                ${getDrugNativeFieldHtml('Location', '<div id="export-location-control"></div>')}
                ${getDrugNativeFieldHtml('Performed By', '<div id="export-performer-control"></div>')}
                ${getDrugNativeFieldHtml('Approved By', '<div id="export-approver-control"></div>')}
            </div>

            <div class="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
                <div class="flex items-center gap-3">
                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Hide Inactive</span>
                    <label class="form-switch">
                        <input type="checkbox" id="export-hide-inactive">
                        <span class="slider round"></span>
                    </label>
                </div>
                <label class="${NATIVE_CHECKBOX_LABEL_CLASSES} justify-start" data-native-checkbox-label>
                    <input type="checkbox" id="export-audit-summaries" class="${NATIVE_CHECKBOX_CLASSES}">
                    <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                        <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
                    </span>
                    <span class="text-sm text-gray-700 dark:text-gray-300">Include Audit Summaries</span>
                </label>
            </div>
            <p id="export-status-msg" class="min-h-4 text-center text-xs text-gray-500" aria-live="polite"></p>
        </div>
    `;

    footer.querySelectorAll('[data-modal-custom-actions="true"]').forEach((element) => element.remove());
    footer.className = 'mt-3 flex flex-col gap-3 px-2 shrink-0 sm:flex-row sm:items-center sm:justify-between';
    submitBtn.style.display = 'none';
    cancelBtn.style.display = '';
    cancelBtn.textContent = 'Close';
    cancelBtn.className = DRUG_CANCEL_BUTTON_CLASSES;
    cancelBtn.onclick = closeForm;

    const actionGroup = document.createElement('div');
    actionGroup.dataset.modalCustomActions = 'true';
    actionGroup.className = 'flex flex-col-reverse gap-3 sm:flex-row sm:justify-end';
    actionGroup.innerHTML = `
        <button type="button" id="btn-export-print" class="${EXPORT_ACTION_BUTTON_CLASSES}">
            <span class="material-symbols-outlined text-lg">print</span>
            Print View
        </button>
        <button type="button" id="btn-export-csv" class="${EXPORT_ACTION_BUTTON_CLASSES}">
            <span class="material-symbols-outlined text-lg">download</span>
            Download CSV
        </button>
    `;
    footer.insertBefore(actionGroup, submitBtn);

    const getDrugOptions = () => (document.getElementById('export-hide-inactive')?.checked ? activeDrugs : allDrugs)
        .map(toExportOption);
    const getLocationOptions = () => (document.getElementById('export-hide-inactive')?.checked ? activeLocations : allLocations)
        .map(toExportOption);
    const getUserOptions = () => (document.getElementById('export-hide-inactive')?.checked ? activeUsers : allUsers)
        .map(toExportOption);

    const createFilterControl = ({
        id,
        options,
        emptyLabel,
        allLabel,
        selectedLabel,
        accessibleName,
    }) => createSelectControl({
        root: document.getElementById(id),
        options,
        value: getExportOptionValues(options),
        multiple: true,
        checkboxMenu: true,
        bulkSelection: false,
        searchable: options.length >= EXPORT_FILTER_SEARCH_THRESHOLD,
        searchThreshold: EXPORT_FILTER_SEARCH_THRESHOLD,
        emptySelectionLabel: emptyLabel,
        allSelectedLabel: allLabel,
        selectedCountLabel: selectedLabel,
        accessibleName,
        placeholder: allLabel,
        portalMenu: true,
        mirror: false,
        required: options.length > 0,
        requiredMessage: `Select at least one ${accessibleName.replace(/ filter$/u, '').toLowerCase()}.`,
    });

    let applyingPreset = false;
    const dateRangeControl = createDateRangePreset({
        root: document.getElementById('export-date-range-control'),
        value: { start: firstDayStr, end: today },
        name: 'exportDateRange',
        mirror: false,
        onChange: () => {
            if (!applyingPreset) {
                presetControl.setValue('custom');
            }
        },
    });
    const presetControl = createSelectControl({
        root: document.getElementById('export-date-preset-control'),
        options: DATE_PRESET_OPTIONS,
        value: 'month_to_date',
        accessibleName: 'Date range preset',
        searchable: false,
        searchThreshold: EXPORT_FILTER_SEARCH_THRESHOLD,
        mirror: false,
        onChange: (preset) => {
            const nextRange = getDatePresetRange(preset);
            if (!nextRange) {return;}
            applyingPreset = true;
            dateRangeControl.setValue(nextRange);
            applyingPreset = false;
        },
    });

    const controls = {
        types: createFilterControl({
            id: 'export-type-control',
            options: typeOptions.map(toExportOption),
            emptyLabel: 'No types',
            allLabel: 'All types',
            selectedLabel: 'Selected Types',
            accessibleName: 'Transaction type filter',
        }),
        drugs: createFilterControl({
            id: 'export-drug-control',
            options: getDrugOptions(),
            emptyLabel: 'No drugs',
            allLabel: 'All drugs',
            selectedLabel: 'Selected Drugs',
            accessibleName: 'Drug filter',
        }),
        locations: createFilterControl({
            id: 'export-location-control',
            options: getLocationOptions(),
            emptyLabel: 'No locations',
            allLabel: 'All locations',
            selectedLabel: 'Selected Locations',
            accessibleName: 'Location filter',
        }),
        performers: createFilterControl({
            id: 'export-performer-control',
            options: getUserOptions(),
            emptyLabel: 'No users',
            allLabel: 'All users',
            selectedLabel: 'Selected Users',
            accessibleName: 'Performed by filter',
        }),
        approvers: createFilterControl({
            id: 'export-approver-control',
            options: getUserOptions(),
            emptyLabel: 'No users',
            allLabel: 'All users',
            selectedLabel: 'Selected Users',
            accessibleName: 'Approved by filter',
        }),
    };

    bindDrugNativeFieldSurface(itemForm);

    const inactiveToggle = document.getElementById('export-hide-inactive');
    inactiveToggle.onchange = () => {
        const drugOptions = getDrugOptions();
        const locationOptions = getLocationOptions();
        const userOptions = getUserOptions();
        controls.drugs.update({
            options: drugOptions,
            value: keepSelectedExportValues(controls.drugs, drugOptions),
        });
        controls.locations.update({
            options: locationOptions,
            value: keepSelectedExportValues(controls.locations, locationOptions),
        });
        controls.performers.update({
            options: userOptions,
            value: keepSelectedExportValues(controls.performers, userOptions),
        });
        controls.approvers.update({
            options: userOptions,
            value: keepSelectedExportValues(controls.approvers, userOptions),
        });
    };

    const statusMsg = document.getElementById('export-status-msg');
    const csvButton = document.getElementById('btn-export-csv');
    const printButton = document.getElementById('btn-export-print');
    const exportButtons = [csvButton, printButton].filter(Boolean);
    const defaultButtonHtml = new Map(
        exportButtons.map((button) => [button, button.innerHTML]),
    );

    const setStatus = (message, tone = 'neutral') => {
        statusMsg.textContent = message;
        statusMsg.className = [
            'min-h-4 text-center text-xs',
            tone === 'success' ? 'text-green-600' : '',
            tone === 'error' ? 'text-red-600' : '',
            tone === 'loading' ? 'text-blue-600 animate-pulse' : '',
            tone === 'neutral' ? 'text-gray-500' : '',
        ].filter(Boolean).join(' ');
    };

    const setGenerating = (isGenerating, activeButton = null) => {
        exportButtons.forEach((button) => {
            button.disabled = isGenerating;
            button.classList.toggle('opacity-70', isGenerating);
            button.classList.toggle('cursor-wait', isGenerating);
            if (isGenerating) {
                const rect = button.getBoundingClientRect();
                button.style.width = `${rect.width}px`;
                button.style.height = `${rect.height}px`;
                if (button === activeButton) {
                    button.innerHTML = `
                        <span class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
                        Generating
                    `;
                }
                return;
            }

            button.innerHTML = defaultButtonHtml.get(button) || button.innerHTML;
            button.style.width = '';
            button.style.height = '';
        });
    };

    const handleGenerate = async (format) => {
        if (!dateRangeControl.validate() || !validateDrugNativeFields(itemForm)) {
            setStatus('Check the highlighted fields.', 'error');
            return;
        }

        const range = dateRangeControl.value || {};
        const drugOptions = getDrugOptions();
        const locationOptions = getLocationOptions();
        const userOptions = getUserOptions();
        const filters = {
            startDate: range.start || '',
            endDate: range.end || '',
            types: getFilteredSelection(controls.types, typeOptions.map(toExportOption)),
            drugs: getFilteredSelection(controls.drugs, drugOptions),
            locations: getFilteredSelection(controls.locations, locationOptions),
            performer: getFilteredSelection(controls.performers, userOptions),
            approver: getFilteredSelection(controls.approvers, userOptions),
            hideInactive: document.getElementById('export-hide-inactive').checked,
            includeAudits: document.getElementById('export-audit-summaries').checked,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        const refinedFilters = {
            ...filters,
            drugIds: filters.drugs,
            locationIds: filters.locations,
            userIds: filters.performer,
            approverIds: filters.approver,
            includeAudits: filters.includeAudits
        };

        setStatus('', 'neutral');
        setGenerating(true, format === 'print' ? printButton : csvButton);

        try {
            const result = await exportDrugData({
                format: format === 'print' ? 'json' : format,
                filters: refinedFilters
            });

            if (result?.success) {
                if (format === 'csv') {
                    if (typeof result.data === 'string') {
                        const blob = new Blob([result.data], { type: 'text/csv' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `drug_export_${getLocalDateString()}.csv`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                    }
                } else if (format === 'print') {
                    printReport(result.data, result.reportMeta);
                }
            } else {
                throw new Error(result?.error || 'Export failed.');
            }
        } catch (error) {
            console.error(error);
            setStatus(`Error: ${error.message}`, 'error');
        } finally {
            setGenerating(false);
        }
    };

    csvButton.onclick = () => handleGenerate('csv');
    printButton.onclick = () => handleGenerate('print');

    formModalElement.classList.add('flex');
}
